import {
  combineLatest,
  distinctUntilChanged,
  map,
  tap,
  type Observable,
} from 'rxjs';
import type { I18nLocale } from '@ardoq/i18n';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { type ArdoqId, type GroupingRule, ViewIds } from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import type { GraphComponentViewLegendProps } from '@ardoq/view-legend';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { graphGroupingRules$ } from 'streams/grouping/graphGroupingRules$';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import type { GroupFoldState } from '@ardoq/graph-view';
import {
  assistantDiagram$,
  AssistantDiagramState,
  assistantDiagramZoomIn,
  assistantDiagramZoomOut,
  optimizeAssistantDiagramLayout,
} from './assistantDiagram$';
import { applyRecipe, AssistantDiagramGraphProps } from './applyRecipe';
import {
  EMPTY_LEGEND_PROPS,
  toScopeDataLegendProps,
} from 'tabview/assistantShared/legend';
import {
  buildStyleByModelId,
  type ModelIdStyle,
} from 'tabview/assistantShared/buildStyleByModelId';
import { registerViewPane } from 'tabview/assistantShared/registerViewPane';

export type AssistantDiagramViewModel = {
  i18nLocale: I18nLocale;
  /** GraphView props derived from the current recipe + in-view graph, or null if no recipe yet. */
  graphProps: AssistantDiagramGraphProps | null;
  /** The recipe's title, shown as a header above the diagram. */
  title: string | null;
  /** A failure to surface in place of the empty state when no diagram could be produced. */
  error: string | null;
  isLegendActive: boolean;
  legendProps: GraphComponentViewLegendProps;
  /** Live group-folding summary, driving the collapse-all/expand-all view-pane buttons. */
  foldState: GroupFoldState;
};

/** Everything but `foldState` — the graph-shaping part that must not recompute on a fold. */
type AssistantDiagramRenderModel = Omit<AssistantDiagramViewModel, 'foldState'>;

const toRenderModel = (
  state: AssistantDiagramState,
  i18nLocale: I18nLocale,
  scopeData: EnhancedScopeData,
  isLegendActive: boolean,
  groupingRules: GroupingRule[],
  styleByModelId: Map<ArdoqId, ModelIdStyle>
): AssistantDiagramRenderModel => {
  const graphProps = state.recipe
    ? applyRecipe(
        scopeData,
        styleByModelId,
        state.recipe,
        groupingRules,
        i18nLocale
      )
    : null;
  return {
    i18nLocale,
    graphProps,
    title: state.recipe?.title ?? null,
    error: state.error,
    isLegendActive,
    legendProps: graphProps
      ? toScopeDataLegendProps(scopeData)
      : EMPTY_LEGEND_PROPS,
  };
};

const VIEW_ID = ViewIds.ASSISTANT_DIAGRAM;

const viewSettingsWithChanges$ = getViewpointModeViewSettingsWithChanges$(
  VIEW_ID,
  toViewpointModeMainViewInstanceId(VIEW_ID)
);

const renderModel$ = combineLatest([
  assistantDiagram$,
  i18nLocale$,
  loadedGraph$,
  viewSettingsWithChanges$,
  graphGroupingRules$,
  formattingAndLabelRules$,
]).pipe(
  // In viewpoint mode `loadedGraph$.scopeData` is the enhanced source of truth: its
  // scope component ids and `componentsById` come from the same loaded snapshot, so
  // they can't be transiently out of sync. Only recompute (and risk a yFiles
  // relayout) when an input that shapes the diagram changes by reference — including
  // conditional-formatting rules, which change node colors. `foldState` is
  // deliberately excluded — it's attached below without re-running `applyRecipe`.
  distinctUntilChanged(
    (
      [
        prevState,
        prevLocale,
        prevLoadedGraph,
        prevSettings,
        prevGroupingRules,
        prevFormattingRules,
      ],
      [state, locale, loadedGraph, settings, groupingRules, formattingRules]
    ) =>
      prevState.recipe === state.recipe &&
      prevState.error === state.error &&
      prevLocale === locale &&
      prevLoadedGraph.scopeData === loadedGraph.scopeData &&
      prevSettings.currentSettings.isLegendActive ===
        settings.currentSettings.isLegendActive &&
      prevGroupingRules === groupingRules &&
      prevFormattingRules === formattingRules
  ),
  map(
    ([
      state,
      i18nLocale,
      loadedGraph,
      settings,
      groupingRules,
      formattingRules,
    ]) =>
      toRenderModel(
        state,
        i18nLocale,
        loadedGraph.scopeData,
        settings.currentSettings.isLegendActive,
        groupingRules,
        buildStyleByModelId(
          loadedGraph.scopeData,
          formattingRules.rules,
          'icon'
        )
      )
  )
);

// Attach the live fold state as its own stage so a fold updates the collapse/expand-all
// buttons (via `registerViewPane`) without re-emitting `renderModel$` — that keeps
// `graphProps` referentially stable, so GraphView never rebuilds/relayouts on a fold.
export const viewModel$: Observable<AssistantDiagramViewModel> = combineLatest([
  renderModel$,
  assistantDiagram$.pipe(
    map(state => state.foldState),
    distinctUntilChanged(
      (prev, next) =>
        prev.groupCount === next.groupCount &&
        prev.collapsedCount === next.collapsedCount
    )
  ),
]).pipe(
  map(([renderModel, foldState]) => ({ ...renderModel, foldState })),
  // Side effect (registers view-pane actions) kept out of the pure `map` above and out
  // of the React component; runs while the view is subscribed via `connect`.
  tap(viewModel =>
    registerViewPane({
      viewId: VIEW_ID,
      viewProperties: viewModel,
      isLegendActive: viewModel.isLegendActive,
      optimizeLayout: () => dispatchAction(optimizeAssistantDiagramLayout()),
      zoomIn: () => dispatchAction(assistantDiagramZoomIn()),
      zoomOut: () => dispatchAction(assistantDiagramZoomOut()),
    })
  )
);
